import React, { useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import { useFormik } from 'formik';
import { Moment } from 'moment';
import 'react-calendar/dist/Calendar.css';

import {
  activeDate,
  setActiveWebView,
  CalendarStateKey,
  getActiveWebView,
  successOrSelector,
  pharmacistActions,
  pharmacyActions,
  reset,
  getNearbyProps,
  AvailabilityTypes,
  WatchlistedVariants,
  MiscType,
  getTypeOfUser,
  TypeOfUser,
  HelpTypes,
} from '@pharmaplan/common';
import { ReactComponent as FilledFilter } from '@pharmaplan/common/assets/icons/searchIconGreen.svg';
import { ReactComponent as UnfilledFilter } from '@pharmaplan/common/assets/icons/searchIcon.svg';
import { ReactComponent as CloseFilter } from '@pharmaplan/common/assets/icons/close.svg';
import { ReactComponent as PlusIcon } from '@pharmaplan/common/assets/icons/webPlus.svg';

import { ReactComponent as DeleteIcon } from '../../../assets/svg/deleteIcon.svg';

import CustomDropdown from '../../common/CustomDropdown';
import CustomButton from '../../common/CustomButton';
import CustomCheckbox from '../../common/CustomCheckbox';
import Watchlisted from '../../common/Watchlist/Watchlisted';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useAdmin from '../../../hooks/useAdmin';
import useNearbyPharmacies from '../../../hooks/Toolbar/useNearbyPharmacies';
import useMultipleSave from '../../../hooks/useMultipleSave';
import useMultipleDelete from '../../../hooks/useMultipleDelete';
import useAdminContinueCreation from '../../../hooks/Admin/useAdminContinueCreation';
import useProfessional from '../../../hooks/Professional/useProfessional';

import {
  setMultipleAddEvents,
  setTempEvents,
} from '../../../reducers/mainCalendarReducer';
import { resetDialog } from '../../../reducers/dialogReducer';
import {
  getMultipleEventAdd,
  getTempEvents,
} from '../../../selectors/calendarSelector';
import ThemeConstants from '../../../theme/ThemeConstants';
import { MonthWeek } from '../../../helpers/Functions';
import {
  AgencyDashboardDropdown,
  Constants,
  ContractTypeDashboard,
  ProfessionalTypesDropdown,
} from '../../../helpers/Constants';
import genericClasses from '../../../theme/GenericClasses';
import strings from '../../../localization';

import DashboardFilter from './DashboardFilter';
import DayCalendar from './DayCalendar';
import YearCalendar from './YearCalendar';
import NavigateButtons from './NavigateButtons';
import CalendarMapToggle from './CalendarMapToggle';
import ToolbarAdd from './ToolbarAdd';
import classes from './style';
import useSelfService from '../../../hooks/useSelfService';
import AdminFilters from './AdminFilters/AdminFilters';
import useAdminCalendarDropdowns from '../../../hooks/Admin/AdminFilters/useAdminCalendarDropdowns';

export interface IOnNavigate {
  setDate: (date: Moment) => void;
}

interface IToolbarProps {
  title: string | Array<string>;
}

const Toolbar = ({ title }: IToolbarProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const tempEvents = useAppSelector(getTempEvents);

  const { isAdmin } = useAdmin();
  const { isSelfService } = useSelfService();
  const { showConfirmModal } = useAdminContinueCreation();
  const {
    handleMapProfessionalFilter,
    handleMenuClick,
    handlePostedMenuClick,
  } = useAdminCalendarDropdowns();

  const {
    deleteSelectedAvailabilities,
    getDeletedAvailabilities,
    getDeletedVacations,
    handleDeleteReset,
  } = useMultipleDelete();

  const { handleSave } = useMultipleSave();

  const dayCalendarRef = useRef<{ reset:() => void }>();
  const ref = useRef<{ reset:() => void; clear: () => void }>();
  const userType = useAppSelector(getTypeOfUser);
  const nearbyProps = useAppSelector(getNearbyProps);

  const [filterState, setFilterState] = useState({
    submitted: false,
    showFilter: false,
  });
  const { checkForLocation } = useNearbyPharmacies();
  const date = useAppSelector(activeDate);
  const activeView = useAppSelector(getActiveWebView);
  const multipleEventAdd = useAppSelector(getMultipleEventAdd);

  const success = useAppSelector((st) =>
    successOrSelector(
      [
        pharmacistActions.createAvailability,
        pharmacyActions.createWorkshift,
        pharmacistActions.vacations,
      ],
      st,
    ));

  const { submitted, showFilter } = filterState;
  const { pathname } = location;
  const {
    paths: { mapDashboard, admin: adminPaths },
  } = Constants;

  const { adminMapDashboard } = adminPaths;

  const { isProfessional } = useProfessional();
  const pharmacistToggle = pathname === mapDashboard;
  const adminMapToggle = pathname === adminMapDashboard;
  const mapToggle = isAdmin ? adminMapToggle : pharmacistToggle;
  const isPharmacy = userType === TypeOfUser.pharmacy;
  const showUserTypeFilter = isAdmin || isPharmacy;

  const hideMonthWeek = !mapToggle;

  const showMap = isProfessional || isAdmin;

  const shouldShowSelectButton = multipleEventAdd;

  const disableSave = tempEvents.filter(
    (item: { type: string }) =>
      item.type === AvailabilityTypes.tempAvailability,
  ).length === 0;

  const disableDelete = getDeletedAvailabilities().length === 0
    && getDeletedVacations().length === 0;
  const disableReset = disableSave && isAdmin && disableDelete;

  const formik = useFormik({
    initialValues: {
      calendarState: activeView.toLowerCase(),
      shiftsType: MiscType.All,
      postedBy: MiscType.All,
      replacementType: MiscType.All,
    },
    enableReinitialize: true,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const handleReset = () => {
    dispatch(setTempEvents([]));
    handleDeleteReset();
  };

  const handleFilterState = () => {
    setFilterState({
      ...filterState,
      showFilter: !showFilter,
    });
  };

  const filterStates = () => {
    switch (true) {
      case !submitted && !showFilter:
        return (
          <IconButton onClick={handleFilterState}>
            <UnfilledFilter id="filter" />
          </IconButton>
        );
      case !submitted && showFilter:
        return (
          <IconButton
            onClick={() => {
              handleFilterState();
              ref.current?.clear();
            }}
          >
            <CloseFilter fill={ThemeConstants.grey[800]} />
          </IconButton>
        );
      case submitted:
        return (
          <IconButton onClick={handleFilterState}>
            <FilledFilter />
          </IconButton>
        );
      default:
        return null;
    }
  };

  const {
    values: { calendarState },
  } = formik ?? {};

  useEffect(() => {
    if (success && multipleEventAdd) {
      handleReset();
      dispatch(resetDialog());
      dispatch(reset());
    }

    return () => {
      dispatch(setMultipleAddEvents(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    dispatch(setActiveWebView(calendarState as CalendarStateKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarState, date]);

  const showFilterCheck = showMap && !multipleEventAdd;

  return (
    <Box sx={classes.toolbarContainer}>
      {!isAdmin && (
        <Box sx={classes.toolbarHeadingContainer}>
          <Typography variant="h5" sx={classes.calendarHeading}>
            {title}
          </Typography>
          <Box sx={genericClasses.flex}>
            {showFilterCheck && (
              <Box sx={classes.filterCont}>
                {submitted && (
                  <CustomButton
                    customClass={classes.clearButton}
                    label={strings.clear}
                    variant="outlined"
                    onClick={() => {
                      ref?.current?.reset();
                      dayCalendarRef.current?.reset();
                    }}
                  />
                )}

                {isProfessional && !mapToggle && (
                  <Watchlisted variant={WatchlistedVariants.interactive} />
                )}

                <Box sx={classes.searchIcon}>{filterStates()}</Box>
              </Box>
            )}
            <ToolbarAdd />
          </Box>
        </Box>
      )}

      {isProfessional && (
        <DashboardFilter
          isMap={mapToggle}
          filterState={filterState}
          setFilterState={setFilterState}
          ref={ref}
        />
      )}

      <Box sx={classes.navigateDateContainer}>
        <Box sx={classes.navigateDate}>
          <Box
            id="calendarViewDropdown"
            component="div"
            sx={classes.navigationToggle}
          >
            {mapToggle ? (
              <DayCalendar ref={dayCalendarRef} setFiltered={setFilterState} />
            ) : (
              <YearCalendar />
            )}
          </Box>
          <NavigateButtons />
        </Box>

        <Box component="div" sx={classes.toggleViewContainer}>
          {shouldShowSelectButton && (
            <Box sx={classes.multipleSelectButtons}>
              <CustomButton
                startIcon={<CachedIcon />}
                customClass={classes.resetButton}
                onClick={handleReset}
                disabled={disableReset}
                variant={disableReset ? 'contained' : 'outlined'}
                label={strings.reset}
                size="small"
              />

              <CustomButton
                customClass={classes.resetButton}
                startIcon={<PlusIcon />}
                onClick={showConfirmModal(handleSave)}
                disabled={disableSave}
                label={strings.save}
                size="small"
              />

              {isAdmin && (
                <CustomButton
                  size="small"
                  customClass={classes.resetButton}
                  startIcon={
                    <DeleteIcon stroke={ThemeConstants.secondary.main} />
                  }
                  onClick={deleteSelectedAvailabilities}
                  disabled={disableDelete}
                  label={strings.delete}
                  color="error"
                />
              )}
            </Box>
          )}

          {isAdmin && showFilterCheck && (
            <Box sx={classes.filterCont}>
              {submitted && (
                <CustomButton
                  customClass={classes.clearButton}
                  label={strings.clear}
                  variant="outlined"
                  onClick={() => {
                    ref?.current?.reset();
                    dayCalendarRef.current?.reset();
                  }}
                />
              )}
              <Box sx={classes.searchIcon}>{filterStates()}</Box>
            </Box>
          )}
          {mapToggle && !isAdmin && (
            <CustomCheckbox
              label={strings.pharmaciesNearby}
              name="toggle"
              value={nearbyProps.showNearBy}
              labelClass={classes.checkboxLabel}
              handleChange={checkForLocation}
              id="toggle"
            />
          )}

          {showMap && <CalendarMapToggle />}

          {showUserTypeFilter && (
            <CustomDropdown
              name="replacementType"
              menuItems={[...ProfessionalTypesDropdown()]}
              formik={formik}
              onClickMenuItem={handleMapProfessionalFilter}
            />
          )}

          {hideMonthWeek && (
            <>
              {isAdmin && isSelfService && (
                <CustomDropdown
                  id="postedBy"
                  name="postedBy"
                  formik={formik}
                  onClickMenuItem={handlePostedMenuClick}
                  customClass={classes.dropdownCustomHeight}
                  menuItems={AgencyDashboardDropdown()}
                  placeholder={Constants.null}
                />
              )}
              <CustomDropdown
                id="shiftsType"
                name="shiftsType"
                formik={formik}
                onClickMenuItem={handleMenuClick}
                customClass={classes.dropdownCustomHeight}
                menuItems={ContractTypeDashboard()}
                placeholder={Constants.null}
              />
              <CustomDropdown
                id="monthWeek"
                name="calendarState"
                formik={formik}
                customClass={classes.dropdownCustomHeight}
                menuItems={MonthWeek()}
                placeholder={Constants.null}
              />
            </>
          )}
        </Box>
      </Box>
      {isAdmin && filterState.showFilter && (
        <Box sx={classes.adminFilterContainer}>
          <AdminFilters
            isMap={mapToggle}
            filterState={filterState}
            setFilterState={setFilterState}
            ref={ref}
          />
        </Box>
      )}
    </Box>
  );
};

export default Toolbar;
